import {
  AttendeesApi,
  ErrorResponse,
  FeedbackRating,
  FindProgramAttendeesRequest,
  ProgramAttendee,
  ProgramAttendeeList,
  ProgramAttendeeUpdateParams,
} from '@citruscamps/citrus-client'
import { DateTime } from 'luxon'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult, useQueryClient } from 'react-query'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
} from '../../../../../constants/pagination'
import {
  IFetchManyProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { useToast } from '../../../../../hooks/useToast'
import {
  DateRangeType,
  FilterDateRange,
  FilterNumberRange,
} from '../../../../../interfaces/filter-range'
import { Pagination } from '../../../../../interfaces/pagination'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { FeedbackRatingNone } from '../../../../Financials/pages/FinancialReportDetails/components/RegistrationFilterButton'

export const DefaultAttendeeFetchProps: AttendeeFetchProps = {
  sort: 'first_name',
  order: 'ASC',
  query: {},
}

type FeedbackRatingAttributes = 'feedback_rating'
type AgeAttributes = `age_${keyof FilterNumberRange}` | 'age'
type BirthdayAttributes = `birthday_${keyof FilterNumberRange}`
type DateOfBirthAttributes = `date_of_birth_${keyof FilterDateRange}`
type RegistrationCountAttributes = `num_registrations_${keyof FilterNumberRange}`
type FirstAttendedAttributes = `first_attended_${keyof FilterDateRange}`
type LastAttendedAttributes = `last_attended_${keyof FilterDateRange}`
type CreatedAttributes = `created_${keyof FilterDateRange}`
type UpdatedAttributes = `updated_${keyof FilterDateRange}`
export interface AttendeeFilterProps
  extends Omit<
    FindProgramAttendeesRequest,
    | NonQueryAttributes
    | FeedbackRatingAttributes
    | AgeAttributes
    | BirthdayAttributes
    | DateOfBirthAttributes
    | RegistrationCountAttributes
    | FirstAttendedAttributes
    | LastAttendedAttributes
    | CreatedAttributes
    | UpdatedAttributes
  > {
  feedback_rating?: FeedbackRatingNone[]
  age?: FilterNumberRange
  birthday?: FilterDateRange
  date_of_birth?: FilterDateRange
  birthday_type?: DateRangeType
  registration_count?: FilterNumberRange
  first_attended?: FilterDateRange
  first_attended_type?: DateRangeType
  last_attended?: FilterDateRange
  last_attended_type?: DateRangeType
  created?: FilterDateRange
  created_type?: DateRangeType
  updated?: FilterDateRange
  updated_type?: DateRangeType
}

export interface AttendeeFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: AttendeeFilterProps
}

const useUpdateAttendees = ({ programId }: { programId?: string }) => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()

  const handleUpdate = async (value: ProgramAttendeeUpdateParams) => {
    const client = new AttendeesApi(generateApiConfig())
    if (programId && value?.id) {
      await requestHandler<ProgramAttendee>(
        () =>
          client.updateProgramAttendee({
            program_id: programId,
            program_attendee_id: value.id || '',
            ProgramAttendeeUpdateParams: value,
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'program_attendee',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleArchive = async (value: ProgramAttendeeUpdateParams) => {
    const client = new AttendeesApi(generateApiConfig())
    if (programId && value?.id) {
      await requestHandler<ProgramAttendee>(
        () =>
          client.updateProgramAttendee({
            program_id: programId,
            program_attendee_id: value.id || '',
            ProgramAttendeeUpdateParams: {
              ...value,
              is_archived: true,
            },
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'program_attendee',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleUnarchive = async (value: ProgramAttendeeUpdateParams) => {
    const client = new AttendeesApi(generateApiConfig())
    if (programId && value?.id) {
      await requestHandler<ProgramAttendee>(
        () =>
          client.updateProgramAttendee({
            program_id: programId,
            program_attendee_id: value.id || '',
            ProgramAttendeeUpdateParams: {
              ...value,
              is_archived: false,
            },
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'program_attendee',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  return {
    handleUpdate,
    handleArchive,
    handleUnarchive,
  }
}

type IProps = Pick<
  IFetchManyProps<ProgramAttendee, AttendeeFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'setFetchProps' | 'isEnabled'
>

interface FetchAttendees {
  data: ProgramAttendee[]
  error?: ErrorResponse
  fetchProps: AttendeeFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handleUpdate: (value: ProgramAttendeeUpdateParams) => Promise<void>
  handleArchive: (value: ProgramAttendee) => Promise<void>
  handleUnarchive: (value: ProgramAttendee) => Promise<void>
  setFetchProps: (value?: AttendeeFetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchAttendees = (props: IProps): FetchAttendees => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<ProgramAttendee, AttendeeFilterProps>({
    type: 'program_attendee',
    programId: props.programId,
    fetchProps: props.fetchProps,
    defaultFetchProps: DefaultAttendeeFetchProps,
    defaultPagination: props.pagination || DefaultMainPagination,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<ProgramAttendeeList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new AttendeesApi(generateApiConfig())
      return await client.findProgramAttendees(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          contact_id: query?.contact_id || undefined,
          has_account: typeof query?.has_account !== 'undefined' ? query?.has_account : undefined,
          gender: query?.gender && query?.gender.length > 0 ? query.gender : undefined,
          membership_package_id:
            query?.membership_package_id && query?.membership_package_id.length > 0
              ? query.membership_package_id
              : undefined,
          is_archived: typeof query?.is_archived !== 'undefined' ? query?.is_archived : undefined,
          age_lt: query?.age?.lt,
          age_gt: query?.age?.gt,
          age_lte: query?.age?.lte,
          age_gte: query?.age?.gte,
          date_of_birth_lt: query?.date_of_birth?.lt
            ? (typeof query?.date_of_birth?.lt === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.lt)
                : DateTime.fromJSDate(query?.date_of_birth?.lt)
              ).toJSDate()
            : undefined,
          date_of_birth_gt: query?.date_of_birth?.gt
            ? (typeof query?.date_of_birth?.gt === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.gt)
                : DateTime.fromJSDate(query?.date_of_birth?.gt)
              ).toJSDate()
            : undefined,
          date_of_birth_lte: query?.date_of_birth?.lte
            ? (typeof query?.date_of_birth?.lte === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.lte)
                : DateTime.fromJSDate(query?.date_of_birth?.lte)
              ).toJSDate()
            : undefined,
          date_of_birth_gte: query?.date_of_birth?.gte
            ? (typeof query?.date_of_birth?.gte === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.gte)
                : DateTime.fromJSDate(query?.date_of_birth?.gte)
              ).toJSDate()
            : undefined,
          birthday_lt: query?.birthday?.lt
            ? (typeof query?.birthday?.lt === 'string'
                ? DateTime.fromISO(query?.birthday?.lt)
                : DateTime.fromJSDate(query?.birthday?.lt)
              ).toJSDate()
            : undefined,
          birthday_gt: query?.birthday?.gt
            ? (typeof query?.birthday?.gt === 'string'
                ? DateTime.fromISO(query?.birthday?.gt)
                : DateTime.fromJSDate(query?.birthday?.gt)
              ).toJSDate()
            : undefined,
          birthday_lte: query?.birthday?.lte
            ? (typeof query?.birthday?.lte === 'string'
                ? DateTime.fromISO(query?.birthday?.lte)
                : DateTime.fromJSDate(query?.birthday?.lte)
              ).toJSDate()
            : undefined,
          birthday_gte: query?.birthday?.gte
            ? (typeof query?.birthday?.gte === 'string'
                ? DateTime.fromISO(query?.birthday?.gte)
                : DateTime.fromJSDate(query?.birthday?.gte)
              ).toJSDate()
            : undefined,
          feedback_rating:
            query?.feedback_rating && query?.feedback_rating.length > 0
              ? query?.feedback_rating.map<FeedbackRating>((rating) =>
                  rating === 'none' ? (undefined as any) : rating,
                )
              : undefined,
          num_registrations_lt: query?.registration_count?.lt || undefined,
          num_registrations_gt: query?.registration_count?.gt || undefined,
          num_registrations_lte: query?.registration_count?.lte || undefined,
          num_registrations_gte: query?.registration_count?.gte || undefined,
          first_attended_lt: query?.first_attended?.lt
            ? (typeof query?.first_attended?.lt === 'string'
                ? DateTime.fromISO(query?.first_attended?.lt)
                : DateTime.fromJSDate(query?.first_attended?.lt)
              ).toJSDate()
            : undefined,
          first_attended_gt: query?.first_attended?.gt
            ? (typeof query?.first_attended?.gt === 'string'
                ? DateTime.fromISO(query?.first_attended?.gt)
                : DateTime.fromJSDate(query?.first_attended?.gt)
              ).toJSDate()
            : undefined,
          first_attended_lte: query?.first_attended?.lte
            ? (typeof query?.first_attended?.lte === 'string'
                ? DateTime.fromISO(query?.first_attended?.lte)
                : DateTime.fromJSDate(query?.first_attended?.lte)
              ).toJSDate()
            : undefined,
          first_attended_gte: query?.first_attended?.gte
            ? (typeof query?.first_attended?.gte === 'string'
                ? DateTime.fromISO(query?.first_attended?.gte)
                : DateTime.fromJSDate(query?.first_attended?.gte)
              ).toJSDate()
            : undefined,
          last_attended_lt: query?.last_attended?.lt
            ? (typeof query?.last_attended?.lt === 'string'
                ? DateTime.fromISO(query?.last_attended?.lt)
                : DateTime.fromJSDate(query?.last_attended?.lt)
              ).toJSDate()
            : undefined,
          last_attended_gt: query?.last_attended?.gt
            ? (typeof query?.last_attended?.gt === 'string'
                ? DateTime.fromISO(query?.last_attended?.gt)
                : DateTime.fromJSDate(query?.last_attended?.gt)
              ).toJSDate()
            : undefined,
          last_attended_lte: query?.last_attended?.lte
            ? (typeof query?.last_attended?.lte === 'string'
                ? DateTime.fromISO(query?.last_attended?.lte)
                : DateTime.fromJSDate(query?.last_attended?.lte)
              ).toJSDate()
            : undefined,
          last_attended_gte: query?.last_attended?.gte
            ? (typeof query?.last_attended?.gte === 'string'
                ? DateTime.fromISO(query?.last_attended?.gte)
                : DateTime.fromJSDate(query?.last_attended?.gte)
              ).toJSDate()
            : undefined,
          created_lt: query?.created?.lt
            ? (typeof query?.created?.lt === 'string'
                ? DateTime.fromISO(query?.created?.lt)
                : DateTime.fromJSDate(query?.created?.lt)
              ).toJSDate()
            : undefined,
          created_gt: query?.created?.gt
            ? (typeof query?.created?.gt === 'string'
                ? DateTime.fromISO(query?.created?.gt)
                : DateTime.fromJSDate(query?.created?.gt)
              ).toJSDate()
            : undefined,
          created_lte: query?.created?.lte
            ? (typeof query?.created?.lte === 'string'
                ? DateTime.fromISO(query?.created?.lte)
                : DateTime.fromJSDate(query?.created?.lte)
              ).toJSDate()
            : undefined,
          created_gte: query?.created?.gte
            ? (typeof query?.created?.gte === 'string'
                ? DateTime.fromISO(query?.created?.gte)
                : DateTime.fromJSDate(query?.created?.gte)
              ).toJSDate()
            : undefined,
          updated_lt: query?.updated?.lt
            ? (typeof query?.updated?.lt === 'string'
                ? DateTime.fromISO(query?.updated?.lt)
                : DateTime.fromJSDate(query?.updated?.lt)
              ).toJSDate()
            : undefined,
          updated_gt: query?.updated?.gt
            ? (typeof query?.updated?.gt === 'string'
                ? DateTime.fromISO(query?.updated?.gt)
                : DateTime.fromJSDate(query?.updated?.gt)
              ).toJSDate()
            : undefined,
          updated_lte: query?.updated?.lte
            ? (typeof query?.updated?.lte === 'string'
                ? DateTime.fromISO(query?.updated?.lte)
                : DateTime.fromJSDate(query?.updated?.lte)
              ).toJSDate()
            : undefined,
          updated_gte: query?.updated?.gte
            ? (typeof query?.updated?.gte === 'string'
                ? DateTime.fromISO(query?.updated?.gte)
                : DateTime.fromJSDate(query?.updated?.gte)
              ).toJSDate()
            : undefined,
        },
        { signal },
      )
    },
  })

  const { handleUpdate, handleArchive, handleUnarchive } = useUpdateAttendees({
    programId: props.programId,
  })

  return {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    handleUpdate,
    handleArchive,
    handleUnarchive,
    setFetchProps,
    setPagination,
    setSearch,
  }
}

interface IInfiniteProps extends Omit<IProps, 'isFilterPersisted'> {
  fetchAll?: boolean
}

interface FetchInfiniteAttendees extends Omit<FetchAttendees, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<ProgramAttendeeList, ErrorResponse>>
}

export const useFetchInfiniteAttendees = (props: IInfiniteProps): FetchInfiniteAttendees => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<ProgramAttendee, AttendeeFilterProps>({
    type: 'program_attendee',
    programId: props.programId,
    fetchProps: props.fetchProps || DefaultAttendeeFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    fetchAll: props.fetchAll,
    queryFn: async (queryParams): Promise<ProgramAttendeeList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new AttendeesApi(generateApiConfig())
      return await client.findProgramAttendees(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          contact_id: query?.contact_id || undefined,
          has_account: typeof query?.has_account !== 'undefined' ? query?.has_account : undefined,
          gender: query?.gender && query?.gender.length > 0 ? query.gender : undefined,
          membership_package_id:
            query?.membership_package_id && query?.membership_package_id.length > 0
              ? query.membership_package_id
              : undefined,
          is_archived: typeof query?.is_archived !== 'undefined' ? query?.is_archived : undefined,
          age_lt: query?.age?.lt,
          age_gt: query?.age?.gt,
          age_lte: query?.age?.lte,
          age_gte: query?.age?.gte,
          date_of_birth_lt: query?.date_of_birth?.lt
            ? (typeof query?.date_of_birth?.lt === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.lt)
                : DateTime.fromJSDate(query?.date_of_birth?.lt)
              ).toJSDate()
            : undefined,
          date_of_birth_gt: query?.date_of_birth?.gt
            ? (typeof query?.date_of_birth?.gt === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.gt)
                : DateTime.fromJSDate(query?.date_of_birth?.gt)
              ).toJSDate()
            : undefined,
          date_of_birth_lte: query?.date_of_birth?.lte
            ? (typeof query?.date_of_birth?.lte === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.lte)
                : DateTime.fromJSDate(query?.date_of_birth?.lte)
              ).toJSDate()
            : undefined,
          date_of_birth_gte: query?.date_of_birth?.gte
            ? (typeof query?.date_of_birth?.gte === 'string'
                ? DateTime.fromISO(query?.date_of_birth?.gte)
                : DateTime.fromJSDate(query?.date_of_birth?.gte)
              ).toJSDate()
            : undefined,
          birthday_lt: query?.birthday?.lt
            ? (typeof query?.birthday?.lt === 'string'
                ? DateTime.fromISO(query?.birthday?.lt)
                : DateTime.fromJSDate(query?.birthday?.lt)
              ).toJSDate()
            : undefined,
          birthday_gt: query?.birthday?.gt
            ? (typeof query?.birthday?.gt === 'string'
                ? DateTime.fromISO(query?.birthday?.gt)
                : DateTime.fromJSDate(query?.birthday?.gt)
              ).toJSDate()
            : undefined,
          birthday_lte: query?.birthday?.lte
            ? (typeof query?.birthday?.lte === 'string'
                ? DateTime.fromISO(query?.birthday?.lte)
                : DateTime.fromJSDate(query?.birthday?.lte)
              ).toJSDate()
            : undefined,
          birthday_gte: query?.birthday?.gte
            ? (typeof query?.birthday?.gte === 'string'
                ? DateTime.fromISO(query?.birthday?.gte)
                : DateTime.fromJSDate(query?.birthday?.gte)
              ).toJSDate()
            : undefined,
          feedback_rating:
            query?.feedback_rating && query?.feedback_rating.length > 0
              ? query?.feedback_rating.map<FeedbackRating>((rating) =>
                  rating === 'none' ? (undefined as any) : rating,
                )
              : undefined,
          num_registrations_lt: query?.registration_count?.lt || undefined,
          num_registrations_gt: query?.registration_count?.gt || undefined,
          num_registrations_lte: query?.registration_count?.lte || undefined,
          num_registrations_gte: query?.registration_count?.gte || undefined,
          first_attended_lt: query?.first_attended?.lt
            ? (typeof query?.first_attended?.lt === 'string'
                ? DateTime.fromISO(query?.first_attended?.lt)
                : DateTime.fromJSDate(query?.first_attended?.lt)
              ).toJSDate()
            : undefined,
          first_attended_gt: query?.first_attended?.gt
            ? (typeof query?.first_attended?.gt === 'string'
                ? DateTime.fromISO(query?.first_attended?.gt)
                : DateTime.fromJSDate(query?.first_attended?.gt)
              ).toJSDate()
            : undefined,
          first_attended_lte: query?.first_attended?.lte
            ? (typeof query?.first_attended?.lte === 'string'
                ? DateTime.fromISO(query?.first_attended?.lte)
                : DateTime.fromJSDate(query?.first_attended?.lte)
              ).toJSDate()
            : undefined,
          first_attended_gte: query?.first_attended?.gte
            ? (typeof query?.first_attended?.gte === 'string'
                ? DateTime.fromISO(query?.first_attended?.gte)
                : DateTime.fromJSDate(query?.first_attended?.gte)
              ).toJSDate()
            : undefined,
          last_attended_lt: query?.last_attended?.lt
            ? (typeof query?.last_attended?.lt === 'string'
                ? DateTime.fromISO(query?.last_attended?.lt)
                : DateTime.fromJSDate(query?.last_attended?.lt)
              ).toJSDate()
            : undefined,
          last_attended_gt: query?.last_attended?.gt
            ? (typeof query?.last_attended?.gt === 'string'
                ? DateTime.fromISO(query?.last_attended?.gt)
                : DateTime.fromJSDate(query?.last_attended?.gt)
              ).toJSDate()
            : undefined,
          last_attended_lte: query?.last_attended?.lte
            ? (typeof query?.last_attended?.lte === 'string'
                ? DateTime.fromISO(query?.last_attended?.lte)
                : DateTime.fromJSDate(query?.last_attended?.lte)
              ).toJSDate()
            : undefined,
          last_attended_gte: query?.last_attended?.gte
            ? (typeof query?.last_attended?.gte === 'string'
                ? DateTime.fromISO(query?.last_attended?.gte)
                : DateTime.fromJSDate(query?.last_attended?.gte)
              ).toJSDate()
            : undefined,
          created_lt: query?.created?.lt
            ? (typeof query?.created?.lt === 'string'
                ? DateTime.fromISO(query?.created?.lt)
                : DateTime.fromJSDate(query?.created?.lt)
              ).toJSDate()
            : undefined,
          created_gt: query?.created?.gt
            ? (typeof query?.created?.gt === 'string'
                ? DateTime.fromISO(query?.created?.gt)
                : DateTime.fromJSDate(query?.created?.gt)
              ).toJSDate()
            : undefined,
          created_lte: query?.created?.lte
            ? (typeof query?.created?.lte === 'string'
                ? DateTime.fromISO(query?.created?.lte)
                : DateTime.fromJSDate(query?.created?.lte)
              ).toJSDate()
            : undefined,
          created_gte: query?.created?.gte
            ? (typeof query?.created?.gte === 'string'
                ? DateTime.fromISO(query?.created?.gte)
                : DateTime.fromJSDate(query?.created?.gte)
              ).toJSDate()
            : undefined,
          updated_lt: query?.updated?.lt
            ? (typeof query?.updated?.lt === 'string'
                ? DateTime.fromISO(query?.updated?.lt)
                : DateTime.fromJSDate(query?.updated?.lt)
              ).toJSDate()
            : undefined,
          updated_gt: query?.updated?.gt
            ? (typeof query?.updated?.gt === 'string'
                ? DateTime.fromISO(query?.updated?.gt)
                : DateTime.fromJSDate(query?.updated?.gt)
              ).toJSDate()
            : undefined,
          updated_lte: query?.updated?.lte
            ? (typeof query?.updated?.lte === 'string'
                ? DateTime.fromISO(query?.updated?.lte)
                : DateTime.fromJSDate(query?.updated?.lte)
              ).toJSDate()
            : undefined,
          updated_gte: query?.updated?.gte
            ? (typeof query?.updated?.gte === 'string'
                ? DateTime.fromISO(query?.updated?.gte)
                : DateTime.fromJSDate(query?.updated?.gte)
              ).toJSDate()
            : undefined,
        },
        { signal },
      )
    },
  })

  const { handleUpdate, handleArchive, handleUnarchive } = useUpdateAttendees({
    programId: props.programId,
  })

  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    fetchNextPage,
    setSearch,
    handleUpdate,
    handleArchive,
    handleUnarchive,
  }
}
